import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import styles from './selectAsyncFilter.module.scss';
import { Api } from '../../../api/api';
import { config } from '../../../api/config';
import {
  useClickOutside,
  generatePhoneQuery,
  useFixedDropdownPosition,
} from '../../../utils';
import { Button, Icon, Multiselect, Checkbox, Spinner } from '../..';
import FilterTitle from '../_elements/filterTitle/filterTitle';

const SelectAsyncFilter = ({
  title,
  valueType,
  filterState,
  setFilterState,
  onSubmit,
  sortOrder,
  setSortOrder,
  name,
  position,
  isActive,
  params,
  disableSorting,
  disabled,
}) => {
  const [value, setValue] = useState('');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shouldSort, setShouldSort] = useState(false);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        setError('');

        let query;
        if (params.model === 'clients' && params.field === 'phone') {
          query = generatePhoneQuery(value);
        } else {
          query = value;
        }

        const results = await Api.searchHeader(query, params);

        setList(results);

        if (results.length === 0) {
          setError('Rezultatų nėra');
        }
      } catch (err) {
        setError('Klaida, pakartokite paiešką');
      } finally {
        setLoading(false);
      }
    };

    if (value.length >= config.DEFAULTS.symbolsToSearch) {
      const debounceTimeout = setTimeout(
        fetchResults,
        config.DEFAULTS.filterRequestDebounceMs
      );

      return () => clearTimeout(debounceTimeout);
    }

    setLoading(false);
    setList([]);
    setError(`Įveskite ${config.DEFAULTS.symbolsToSearch} simbolius`);
  }, [value, params.field, params.model]);

  const filterRef = useRef();
  const dropdownRef = useRef();
  const [isOpen, setIsOpen] = useClickOutside(filterRef);
  const dropdownStyle = useFixedDropdownPosition({
    triggerRef: filterRef,
    dropdownRef,
    isOpen,
    position,
  });

  const isAllChecked = list.length === filterState.length;

  const handleCheckAll = () => {
    isAllChecked ? setFilterState([]) : setFilterState(list);
  };

  const handleTitleClick = () => {
    if (disabled) {
      return;
    }

    if (!isOpen) {
      setShouldSort(false);
    }
    isOpen ? setIsOpen(false) : setIsOpen(true);
  };

  const handleOrderClick = () => {
    if (disabled) {
      return;
    }

    setShouldSort(true);
    sortOrder === 'asc' ? setSortOrder('desc') : setSortOrder('asc');
  };

  const handleSubmit = () => {
    if (disabled) {
      return;
    }

    setIsOpen(false);
    onSubmit(shouldSort ? { sortBy: name } : {});
  };

  return (
    <div className={styles.container} ref={filterRef}>
      <FilterTitle
        title={title}
        onTitleClick={handleTitleClick}
        isActive={isActive}
        disabled={disabled}
      />

      {isOpen && (
        <div
          ref={dropdownRef}
          className={cn(styles.filter, styles[position])}
          style={dropdownStyle}
        >
          <div className={styles.search}>
            <input
              type="search"
              placeholder="Paieška"
              className={styles.input}
              value={value}
              disabled={disabled}
              onChange={e => setValue(e.target.value)}
            />
            <Icon name="search" classes={{ icon: styles.icon }} />
          </div>

          <div className={styles.list}>
            {loading ? (
              <Spinner />
            ) : (
              <>
                {error && <p className={styles.tip}>{error}</p>}

                {list.length > 0 && (
                  <Checkbox
                    label="Visi"
                    name="Visi"
                    value="-1"
                    checked={isAllChecked}
                    onChange={handleCheckAll}
                  />
                )}

                <Multiselect
                  array={list}
                  type="array"
                  name="name"
                  value="value"
                  state={filterState}
                  setState={setFilterState}
                  valueType={valueType}
                />
              </>
            )}
          </div>

          <div className={styles.actions}>
            {!disableSorting && (
              <Button
                color="light"
                classes={{ button: styles.button }}
                onClick={handleOrderClick}
                disabled={disabled}
              >
                {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
              </Button>
            )}

            <Button
              color="primary"
              classes={{ button: styles.button }}
              onClick={handleSubmit}
              disabled={disabled}
            >
              Filtruoti
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

SelectAsyncFilter.defaultProps = {
  position: 'left',
  disabled: false,
};

SelectAsyncFilter.propTypes = {
  array: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  valueType: PropTypes.string.isRequired,
  filterState: PropTypes.array.isRequired,
  setFilterState: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  sortOrder: PropTypes.oneOf(['asc', 'desc']).isRequired,
  setSortOrder: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  position: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  disableSorting: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default SelectAsyncFilter;
